import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { jobBriefApi } from '../../../services/jobBriefApi';

/**
 * ConditionsChangedBanner — Phase D.
 *
 * Sticky red bar at the top of the Perform screen. When any pending
 * `conditions_changed` event exists in `performState.conditions_changed_pending`,
 * this component:
 *   1. Blocks the rest of the Perform UI (via a full-width overlay in
 *      PerformPhase — this component just renders the banner + modal).
 *   2. Shows what changed + who flagged it + the auto-created revision.
 *
 * When no pending event exists, this component renders a compact STOP
 * button the crew leader can press to flag a change.
 */
export default function ConditionsChangedBanner({ draftId, performState, ownerEmail, onFlagged }) {
  const pending = performState?.conditions_changed_pending || null;
  const [open, setOpen] = useState(false);
  const [what, setWhat] = useState('');
  const [busy, setBusy] = useState(false);

  const flag = useCallback(async () => {
    if (!what.trim() || !draftId) return;
    setBusy(true);
    try {
      const res = await jobBriefApi.flagConditionsChanged(draftId, {
        what_changed: what.trim(),
        flagged_by:   ownerEmail,
      });
      setOpen(false);
      setWhat('');
      onFlagged?.(res);
    } finally {
      setBusy(false);
    }
  }, [what, draftId, ownerEmail, onFlagged]);

  // Escape-to-close the modal (spec requirement).
  useEffect(() => {
    if (!open) return () => {};
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // ── When a change is pending, show the immutable banner ──────────
  if (pending) {
    return (
      <div
        style={{
          padding: '14px 18px',
          borderRadius: 14,
          background: 'linear-gradient(90deg, rgba(220,38,38,0.18) 0%, rgba(15,27,70,0.85) 100%)',
          border: '1px solid rgba(220,38,38,0.55)',
          color: '#fecaca',
          display: 'flex', alignItems: 'flex-start', gap: 12,
        }}
        data-testid="jbv2-conditions-pending-banner"
      >
        <AlertTriangle size={22} color="#fca5a5" />
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: 11, fontWeight: 800, letterSpacing: '0.16em',
            textTransform: 'uppercase', color: '#fecaca',
          }}>
            STOP — Conditions Changed
          </div>
          <div style={{ marginTop: 4, fontSize: 14, color: '#fee2e2' }}>
            {pending.what_changed}
          </div>
          <div style={{ marginTop: 6, fontSize: 11.5, color: '#fca5a5' }}>
            Flagged by {pending.flagged_by} · Revision {(pending.auto_rebrief_revision || 0)} snapshot saved.
            Return to the <strong>Plan</strong> tab, update Job Info / Hazards, then reopen Perform.
          </div>
        </div>
      </div>
    );
  }

  // ── Otherwise, show the STOP trigger ──────────────────────────────
  return (
    <>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="jbv2-conditions-changed-open"
          style={{
            padding: '9px 14px',
            borderRadius: 10,
            border: '1px solid rgba(220,38,38,0.55)',
            background: 'rgba(220,38,38,0.10)',
            color: '#fecaca',
            fontWeight: 800, fontSize: 12, letterSpacing: '0.08em',
            textTransform: 'uppercase',
            display: 'inline-flex', alignItems: 'center', gap: 6,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          <AlertTriangle size={14} /> Stop — conditions changed
        </button>
        <span className="jbv2-hp-row-meta">
          Press to snapshot the current brief, auto-bump to a new revision, and return to Plan.
        </span>
      </div>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="jbv2-conditions-changed-modal"
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(3,7,18,0.72)', backdropFilter: 'blur(6px)',
            zIndex: 60, display: 'grid', placeItems: 'center', padding: 20,
          }}
        >
          <div style={{
            width: 'min(520px, 100%)',
            background: 'rgba(15,27,70,0.98)',
            border: '1px solid rgba(220,38,38,0.55)',
            borderRadius: 16, padding: 20,
            color: '#f1f5f9', display: 'flex', flexDirection: 'column', gap: 12,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <AlertTriangle size={20} color="#fca5a5" />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#fef2f2' }}>
                  Flag conditions changed
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                  This will snapshot the current brief, auto-bump the revision, and drop back to the Plan phase.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Cancel"
                data-testid="jbv2-conditions-changed-cancel"
                style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
              >
                <X size={18} />
              </button>
            </div>
            <label className="jbv2-field">
              <span className="jbv2-field-label">What changed?</span>
              <textarea
                className="jbv2-field-textarea"
                value={what}
                onChange={(e) => setWhat(e.target.value)}
                rows={4}
                placeholder="e.g. Rain started; visibility dropped. Adjacent circuit re-energized without notice. Additional crew joined."
                data-testid="jbv2-conditions-changed-what"
                autoFocus
              />
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="jbv2-row-btn"
                onClick={() => setOpen(false)}
                data-testid="jbv2-conditions-changed-close"
              >Cancel</button>
              <button
                type="button"
                className="jbv2-row-btn"
                style={{
                  background: 'rgba(220,38,38,0.18)',
                  borderColor: 'rgba(220,38,38,0.55)', color: '#fecaca',
                }}
                disabled={busy || !what.trim()}
                onClick={flag}
                data-testid="jbv2-conditions-changed-submit"
              >
                <AlertTriangle size={12} /> {busy ? 'Snapshotting…' : 'Stop & rebrief'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
